// "Calls, Receivables and Payables" combo renderer (RET-4289): stacked Receivables +
// Payables bars under a cyan area-fill Calls line, on a luxon time axis from
// `data.timeSeries` ({x: bucket epoch ms, y: count, amount?: $}). The card's header
// controls drive `mode` (counts/amounts) and the dashed Receivables % overlay.
// (`data.datasets`/`labels`/`amounts` in the payload are the deprecated
// /mobile/v1/calls_charts contract — this renderer ignores them.)
import { Chart } from 'chart.js'

import { chartPalette, barDepthFill, barCapBorderRadius, verticalGradient } from './palette'
import { formatDollar } from '../app/modules/utilities'
import { dragZoomPlugin } from './drag_zoom_plugin'
import { fillGaps, markNoData, noDataPlugin, timeXScaleOptions } from './time_axis'

// The data layer ships the three series keyed by label ("Calls" / "Receivables" /
// "Payables"); pull each out by name so we stay decoupled from their order.
function seriesByLabel(series, label) {
    const match = series.find((dataset) => dataset.label === label)
    return (match && match.data) || []
}

// Receivable rate per bucket, as a 0–100 percentage of calls that were receivable.
// Both series are gap-filled from the same query, so they are index-aligned.
function receivableRates(callsPoints, receivablePoints) {
    return callsPoints.map((point, i) => {
        const n = Number(point.y) || 0
        const r = Number(receivablePoints[i]?.y) || 0
        return { x: point.x, y: n > 0 ? Math.round((r / n) * 100) : 0 }
    })
}

// Compact money for axis ticks ($1.2k) — combo-specific; full dollars in tooltips reuse the
// shared formatDollar so amounts read identically to every other dollar-formatted chart.
function moneyTick(n) {
    const v = Number(n) || 0
    return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`
}

export function buildCallsReceivablesAndPayablesCombo(
    canvas,
    optionsFromParam,
    { mode = 'counts', showReceivables = false, plugins = [] } = {},
) {
    const { timeSeries = [], timeScale = {}, noDataMessage } = optionsFromParam.data || {}
    const palette = chartPalette()
    const isAmounts = mode === 'amounts'

    const noData = markNoData(canvas, timeSeries)

    const intervalMs = (timeScale.intervalSeconds || 0) * 1000
    const gapFilledPoints = (points) => {
        return fillGaps(points, intervalMs, timeScale.windowStart, timeScale.windowEnd)
    }

    const callsPoints = gapFilledPoints(seriesByLabel(timeSeries, 'Calls'))
    const receivablePoints = gapFilledPoints(seriesByLabel(timeSeries, 'Receivables'))
    const payablePoints = gapFilledPoints(seriesByLabel(timeSeries, 'Payables'))

    const barPoints = (points) => {
        return isAmounts ? points.map((point) => ({ x: point.x, y: point.amount ?? 0 })) : points
    }

    // One shared stack: Receivables sits at the bottom, Payables on top (dataset order).
    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        stack: 'calls',
    }

    // The empty state renders axes only: the bar and line controllers have nothing to
    // draw, and Chart.js 4's time-scale bar layout crashes outright on zero points.
    const datasets = noData
        ? []
        : [
              {
                  ...barBase,
                  label: 'Receivables',
                  data: barPoints(receivablePoints),
                  // Shared bar depth gradient (lit cap at the tip → darker base), the same fill
                  // every other bar chart uses; only the hue differs per series.
                  backgroundColor: barDepthFill(palette.green),
                  borderColor: palette.green,
                  order: 2,
              },
              {
                  ...barBase,
                  label: 'Payables',
                  data: barPoints(payablePoints),
                  backgroundColor: barDepthFill(palette.amber),
                  borderColor: palette.amber,
                  order: 2,
              },
              {
                  type: 'line',
                  label: 'Calls',
                  // Calls is always a count. In $ mode it rides on its own axis so the
                  // dollar scale on `y` doesn't flatten it.
                  yAxisID: isAmounts ? 'yCalls' : 'y',
                  data: callsPoints,
                  borderColor: palette.callsLine,
                  // Cyan area fill whose stops flip with color mode (charts.scss).
                  backgroundColor: verticalGradient([
                      [0, palette.areaFillTop],
                      [1, palette.areaFillBottom],
                  ]),
                  tension: 0.35,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  pointBackgroundColor: palette.callsLine,
                  pointBorderColor: '#fff',
                  pointBorderWidth: 1.5,
                  borderJoinStyle: 'round',
                  fill: true,
                  order: 1,
              },
          ]

    if (showReceivables && !noData) {
        datasets.push({
            type: 'line',
            label: 'Receivables %',
            yAxisID: 'yReceivables',
            data: receivableRates(callsPoints, receivablePoints),
            borderColor: palette.violet,
            borderDash: [5, 5],
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointBackgroundColor: palette.violet,
            order: 0,
        })
    }

    return new Chart(canvas, {
        // Inline plugins from the controller (the shared htmlLegend renders the footer chips).
        plugins: [
            ...plugins,
            dragZoomPlugin(intervalMs, palette.callsLine),
            ...(noData
                ? [
                      noDataPlugin(
                          noDataMessage || 'No data for the selected time frame',
                          palette.ink,
                      ),
                  ]
                : []),
        ],
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                // Canvas legend stays off; the shared htmlLegend builds the footer chips from
                // Chart.js' default legend labels (line-vs-box swatch handled generically there).
                legend: { display: false },
                // Tooltip chrome (dark cyan-bordered box) comes from the global theme; only the
                // value formatting is chart-specific.
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = item.parsed.y
                            if (item.dataset.label === 'Receivables %') {
                                return `Receivables %: ${value}%`
                            }
                            const dollars = isAmounts && item.dataset.label !== 'Calls'
                            const formatted = dollars
                                ? formatDollar(value)
                                : Number(value).toLocaleString()
                            return `${item.dataset.label}: ${formatted}`
                        },
                    },
                },
            },
            scales: {
                x: timeXScaleOptions(timeScale, { stacked: true }),
                // Primary value axis: counts, or $ in amounts mode.
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    ticks: {
                        padding: 8,
                        precision: 0,
                        callback: (value) => (isAmounts ? moneyTick(value) : value),
                    },
                },
                // Right-hand count axis for the Calls line — only present in $ mode.
                yCalls: {
                    display: isAmounts,
                    beginAtZero: true,
                    position: 'right',
                    grid: { drawOnChartArea: false },
                    border: { display: false },
                    ticks: { color: palette.callsLine, padding: 6 },
                },
                // Hidden 0–100 axis for the dashed receivables % line.
                yReceivables: { display: false, min: 0, max: 100 },
            },
            elements: {
                bar: {
                    borderRadius: barCapBorderRadius(false, true),
                    borderSkipped: false,
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
